import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import {
  ENTITLEMENTS_CHANGED_EVENT,
  EntitlementsContext,
  type EntitlementsContextValue,
  type GatedFeature,
} from "./entitlements-context";
import { getEntitlements, type EntitlementSummary } from "../services/billingApi";
import { isUnrecoverableApiError } from "../services/apiClient";
import { meetsTier, requiredTierForTemplate } from "../data/planRules";
import { useAuth } from "../hooks/useAuth";

const FEATURE_KEY: Record<
  GatedFeature,
  keyof EntitlementSummary["features"]
> = {
  REORDER_SECTIONS: "canReorderSections",
  ATS_ENGINE: "hasAtsAccess",
  PORTFOLIO: "hasPortfolioAccess",
  AI_CAREER_AGENT: "hasAiCareerAgent",
};

/**
 * Holds the signed-in user's plan, credits and feature flags.
 *
 * Fetched once per session and refreshed on the events that can change it —
 * a completed purchase, a spent download credit, an AI agent query. Everything
 * exposed here is a UI hint; the server enforces the same rules on each call.
 */
export function EntitlementsProvider({ children }: { children: ReactNode }) {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [entitlements, setEntitlements] = useState<EntitlementSummary | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Set when the server rejects us outright (401/403/404). Every later trigger
   * — a finished checkout, a spent credit, an AI query — would otherwise fire
   * another call that is certain to fail the same way.
   *
   * Keyed off `user`, so signing in again clears it.
   */
  const refusedRef = useRef(false);
  const inFlightRef = useRef<Promise<EntitlementSummary | null> | null>(null);

  useEffect(() => {
    refusedRef.current = false;
  }, [user]);

  const refresh = useCallback(async (): Promise<EntitlementSummary | null> => {
    if (!user) {
      setEntitlements(null);
      return null;
    }
    if (refusedRef.current) return null;
    // Several of these events can land in the same tick (export → credit spent
    // → entitlements changed); share one request rather than firing three.
    if (inFlightRef.current) return inFlightRef.current;

    setIsLoading(true);
    setError(null);

    const request = (async () => {
      try {
        const summary = await getEntitlements();
        setEntitlements(summary);
        return summary;
      } catch (err) {
        if (isUnrecoverableApiError(err)) refusedRef.current = true;
        // A failed load must not lock a paying user out of the UI; treat it as
        // "unknown" and let the server be the authority on the actual request.
        setError(err instanceof Error ? err.message : "Failed to load plan");
        return null;
      } finally {
        setIsLoading(false);
        inFlightRef.current = null;
      }
    })();

    inFlightRef.current = request;
    return request;
  }, [user]);

  useEffect(() => {
    if (isAuthLoading) return;
    void refresh();
  }, [isAuthLoading, refresh]);

  // Refreshed by the builder after a successful export so the credit counter
  // in the header updates without a page reload.
  useEffect(() => {
    const handler = () => void refresh();
    window.addEventListener(ENTITLEMENTS_CHANGED_EVENT, handler);
    return () => window.removeEventListener(ENTITLEMENTS_CHANGED_EVENT, handler);
  }, [refresh]);

  const value = useMemo<EntitlementsContextValue>(() => {
    const planTier = entitlements?.planTier ?? "FREE";
    const downloadCredits = entitlements?.hasUnlimitedDownloads
      ? -1
      : (entitlements?.downloadCredits ?? 0);

    const hasFeature = (feature: GatedFeature): boolean =>
      Boolean(entitlements?.features[FEATURE_KEY[feature]]);

    const isTemplateLocked = (templateKey: string): boolean =>
      (entitlements?.creditBreakdown.bonus ?? 0) < 1 &&
      !((entitlements?.premiumDesignDownloads ?? 0) > 0 && requiredTierForTemplate(templateKey) === "PREMIUM") &&
      !meetsTier(planTier, requiredTierForTemplate(templateKey));

    const canDownloadTemplate = (templateKey: string): boolean => {
      if (isTemplateLocked(templateKey)) return false;
      return downloadCredits === -1 || downloadCredits >= 1;
    };

    return {
      entitlements,
      isLoading: isLoading || isAuthLoading,
      error,
      refresh,
      hasFeature,
      canDownloadTemplate,
      isTemplateLocked,
      planTier,
      downloadCredits,
    };
  }, [entitlements, isLoading, isAuthLoading, error, refresh]);

  return (
    <EntitlementsContext.Provider value={value}>
      {children}
    </EntitlementsContext.Provider>
  );
}
